interface SupportTicketModalProps {
  show: boolean
  onClose: () => void
  supportSuccess: boolean
  supportError: string | null
  supportSubmitting: boolean
  supportCategory: string
  onCategoryChange: (val: string) => void
  supportSubject: string
  onSubjectChange: (val: string) => void
  supportMessage: string
  onMessageChange: (val: string) => void
  onSubmit: () => void
}

export default function SupportTicketModal({
  show,
  onClose,
  supportSuccess,
  supportError,
  supportSubmitting,
  supportCategory,
  onCategoryChange,
  supportSubject,
  onSubjectChange,
  supportMessage,
  onMessageChange,
  onSubmit,
}: SupportTicketModalProps) {
  if (!show) return null

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
              </svg>
            </div>
            <div>
              <h2 className="text-white font-bold text-lg">Contact Support</h2>
              <p className="text-blue-100 text-xs">We typically respond within 24 hours</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        {supportSuccess ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-slate-800 mb-2">Ticket Submitted!</h3>
            <p className="text-sm text-slate-500 mb-6">
              Our support team has received your request and will get back to you within 24 hours.
            </p>
            <button onClick={onClose}
              className="px-6 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-colors">
              Close
            </button>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            {supportError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600 font-medium">
                {supportError}
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Category</label>
              <select value={supportCategory} onChange={e => onCategoryChange(e.target.value)}
                className="w-full border border-slate-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white">
                <option value="general">General Question</option>
                <option value="technical">Technical Issue</option>
                <option value="billing">Billing &amp; Account</option>
                <option value="feature_request">Feature Request</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Subject</label>
              <input type="text" value={supportSubject} onChange={e => onSubjectChange(e.target.value)}
                placeholder="Brief description of your issue..."
                className="w-full border border-slate-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                maxLength={200} />
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Message</label>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 mb-2">
                <p className="text-xs text-amber-700"><span className="font-bold">⚠ Do not enter PHI:</span> Do not include patient names, dates of birth, or medical record numbers in your message.</p>
              </div>
              <textarea value={supportMessage} onChange={e => onMessageChange(e.target.value)}
                placeholder="Describe your issue or question in detail..."
                rows={5}
                className="w-full border border-slate-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                maxLength={2000} />
              <p className="text-xs text-slate-400 mt-1 text-right">{supportMessage.length}/2000</p>
            </div>

            <div className="flex gap-3 pt-2">
              <button onClick={onClose}
                className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-600 text-sm font-semibold rounded-xl hover:bg-slate-50 transition-colors">
                Cancel
              </button>
              <button onClick={onSubmit}
                disabled={supportSubmitting || !supportSubject.trim() || !supportMessage.trim()}
                className="flex-1 px-4 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                {supportSubmitting ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Submitting...
                  </>
                ) : 'Submit Ticket'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
